import { useCallback, useEffect, useState } from "react";
import Uppy from "@uppy/core";
import { useUppy } from "@uppy/react";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import ThumbnailGenerator from "@uppy/thumbnail-generator";
import { getCurrentLocaleCode, getLocale } from "../../../_utils/uppyLocale";
import VideoThumbnailGenerator from "../../../_plugins/uppy/video-thumbnail-generator";
import ImportUrlMedia from "../../../_plugins/uppy/import-url-media";
import { translate as trans } from "../Trans/Trans";
import {
  abortMultipartUpload,
  completeMultipartUpload,
  createMultipartUpload,
  listMultipartUploadParts,
  prepareMultipartUploadParts,
} from "../../../_actions/plannerActions";

const getFileMimeType = (file) => file?.type?.split("/")?.[0];

export const isVideoFile = (file) => getFileMimeType(file) === "video";

export const isPhotoFile = (file) => getFileMimeType(file) === "image";

export const getVideoMaxSize = () => 1024 * 1024 * 500;

export const getPhotoMaxSize = () => 1024 * 1024 * 5;

export const getVideoAllowedFileTypes = () => [".mp4", ".mov"];

export const getPhotoAllowedFileTypes = () => [".gif", ".png", ".jpg", ".jpeg"];

export const usePostFilePickerBase = ({ id, ...options }) => {
  const uppy = useUppy(
    () =>
      new Uppy({
        id,
        autoProceed: true,
        restrictions: {
          maxFileSize: 0,
          allowedFileTypes: [],
          maxNumberOfFiles: 0,
        },
        locale: getLocale(getCurrentLocaleCode()),
      })
  );
  const [uppyNote, setUppyNote] = useState("");
  const [uppyPlugins, setUppyPlugins] = useState(["ImportUrlMedia"]);

  const setUppyOptions = useCallback(
    (opts = {}) => {
      uppy.setOptions(opts);

      const uppyRestrictions = uppy.opts?.restrictions;
      const filesInProgress = uppy.getFiles().length;

      if (
        uppyRestrictions?.maxNumberOfFiles &&
        uppyRestrictions?.maxFileSize &&
        uppyRestrictions?.allowedFileTypes?.length
      ) {
        const maxFiles = uppyRestrictions.maxNumberOfFiles - filesInProgress;
        const maxSize = `${uppyRestrictions.maxFileSize / (1024 * 1024)}MB`;
        const extensions = uppyRestrictions.allowedFileTypes.join(", ");
        const message = trans("post_file_picker.info", {
          number_of_files: maxFiles,
          allowed_file_types: extensions,
          file_size: maxSize,
        });

        setUppyNote(message);
      } else {
        setUppyNote("");
      }
    },
    [uppy, uppyPlugins, setUppyPlugins, setUppyNote]
  );

  useEffect(() => {
    setUppyOptions(options || {});
  }, [uppy]);

  return {
    uppy,
    uppyPlugins,
    uppyNote,
    setUppyOptions,
  };
};

export default (options) => {
  const { uppy, ...rest } = usePostFilePickerBase(options);

  useEffect(() => {
    uppy
      .use(AwsS3Multipart, {
        limit: 3,
        createMultipartUpload: (file) =>
          createMultipartUpload(uppy.getFile(file.id)),
        listParts: (file, data) =>
          listMultipartUploadParts(uppy.getFile(file.id), data),
        prepareUploadParts: (file, partData) =>
          prepareMultipartUploadParts(uppy.getFile(file.id), partData),
        abortMultipartUpload: (file, data) =>
          abortMultipartUpload(uppy.getFile(file.id), data),
        completeMultipartUpload: (file, data) =>
          completeMultipartUpload(uppy.getFile(file.id), data, uppy),
      })
      .use(ThumbnailGenerator, {
        thumbnailWidth: 256,
        thumbnailType: "image/png",
      })
      .use(VideoThumbnailGenerator, {
        thumbnailWidth: 256,
        thumbnailType: "image/png",
      })
      .use(ImportUrlMedia, {
        id: "ImportUrlMedia",
      });
  }, [uppy]);

  return {
    uppy,
    ...rest,
  };
};
